import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "../lib/nav";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        solid ? "bg-navy/80 backdrop-blur-xl border-b border-gold/20" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="container-luxe flex items-center justify-between py-5">
        <Link to="/" className="leading-none" data-testid="nav-brand">
          <span className="block font-serif text-lg sm:text-xl text-cream">Academia</span>
          <span className="block text-[0.6rem] sm:text-[0.65rem] uppercase tracking-luxe text-gold">
            Desde el Primer Latido™
          </span>
        </Link>

        <div className="hidden items-center gap-6 xl:flex">
          {NAV.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to}`}
              className={({ isActive }) =>
                `group relative text-[0.82rem] transition-colors hover:text-gold ${
                  isActive ? "text-gold" : "text-cream/80"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-px bg-gold transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
          <a
            href="https://www.skool.com/academiadesdeelprimerlatido-9718/about"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold py-2.5 text-xs"
            data-testid="nav-cta"
          >
            Empezar
          </a>
        </div>

        <button
          className="text-cream xl:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menú"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-gold/20 bg-navy/95 backdrop-blur-xl xl:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="container-luxe flex max-h-[calc(100vh-5rem)] flex-col gap-5 overflow-y-auto py-8">
              {NAV.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `text-left font-serif text-2xl ${isActive ? "text-gold" : "text-cream"}`
                  }
                  data-testid={`nav-mobile-link-${l.to}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href="https://www.skool.com/academiadesdeelprimerlatido-9718/about"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold mb-2 mt-2 self-start"
                data-testid="nav-mobile-cta"
              >
                Empezar
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
